import {PLAYBACK_TIME_UPDATE_INTERVAL_MS} from '../constants';
import {useFleetTrackerCanvasStore} from '../store/useFleetTrackerCanvasStore';
import {useFleetTrackerPlaybackStore} from '../store/useFleetTrackerPlaybackStore';
import {PlayMode, usePlayStore} from '../store/usePlayStore';
import {IFleetTrackerPlaybackService} from './IFleetTrackerPlaybackService';

export class FleetTrackerPlaybackServiceImpl implements IFleetTrackerPlaybackService {
  private playbackStore: typeof useFleetTrackerPlaybackStore;
  private canvasStore: typeof useFleetTrackerCanvasStore;
  private playStore: typeof usePlayStore;

  private lastTimeCommitMs = 0;
  private pendingTime: Date | undefined = undefined;
  private pendingTimeTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  public constructor(
    playbackStore: typeof useFleetTrackerPlaybackStore,
    canvasStore: typeof useFleetTrackerCanvasStore,
    playStore: typeof usePlayStore,
  ) {
    this.playbackStore = playbackStore;
    this.canvasStore = canvasStore;
    this.playStore = playStore;
  }

  initializePlayback(startTime: Date, endTime: Date | undefined): void {
    this.playbackStore.getState().initializePlayback(startTime, endTime);
  }

  // Leading + trailing throttle: the first tick commits immediately, subsequent ticks within the
  // interval only keep the latest value, which is committed once the interval has passed.
  updateCurrentTime(currentTime: Date): void {
    if (this.pendingTimeTimer !== undefined) {
      this.pendingTime = currentTime;
      return;
    }

    const elapsedMs = Date.now() - this.lastTimeCommitMs;
    if (elapsedMs >= PLAYBACK_TIME_UPDATE_INTERVAL_MS) {
      this.commitCurrentTime(currentTime);
      return;
    }

    this.pendingTime = currentTime;
    this.pendingTimeTimer = setTimeout(
      this.flushPendingTime,
      PLAYBACK_TIME_UPDATE_INTERVAL_MS - elapsedMs,
    );
  }

  cancelPendingCurrentTimeUpdate(): void {
    clearTimeout(this.pendingTimeTimer);
    this.pendingTimeTimer = undefined;
    this.pendingTime = undefined;
  }

  changePlayMode(mode: PlayMode, playing: boolean, speed: number): void {
    this.playStore.getState().changeMode(mode);
    this.playbackStore.getState().setPlayState(playing, speed);
  }

  setPlay(playing: boolean): void {
    const { fleetTrackerRef } = this.canvasStore.getState();

    if (playing) fleetTrackerRef?.play();
    else fleetTrackerRef?.pause();

    this.playbackStore.getState().setPlaying(playing);
  }

  setSpeed(speed: number): void {
    this.canvasStore.getState().fleetTrackerRef?.setSpeed(speed);
    this.playbackStore.getState().setCurrentSpeed(speed);
  }

  seek(time: Date): void {
    // Commit right away (bypassing the throttle), otherwise the slider snaps back to the old time
    // until the next throttled update
    this.cancelPendingCurrentTimeUpdate();
    this.commitCurrentTime(time);

    const { fleetTrackerRef } = this.canvasStore.getState();
    fleetTrackerRef?.pause(); // pause without really changing the state. Wait for PlaybackReady event
    fleetTrackerRef?.seek(time);
  }

  private flushPendingTime = (): void => {
    const { pendingTime } = this;
    this.pendingTimeTimer = undefined;
    this.pendingTime = undefined;

    if (pendingTime) this.commitCurrentTime(pendingTime);
  };

  private commitCurrentTime(currentTime: Date): void {
    this.lastTimeCommitMs = Date.now();
    this.playbackStore.getState().updateCurrentTime(currentTime);
  }
}
